/**
 * Digisaka API config for auth (Bearer token) used by explorer-fields and related endpoints.
 * Override with VITE_DIGISAKA_API_TOKEN in .env for different environments.
 */
export const DIGISAKA_API_TOKEN =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_DIGISAKA_API_TOKEN) ||
  '2550|A69nLrYeGgcJkVjiuVlK9WrF0pBbnNb3JpTsEN93002dd78a';

export function getDigisakaAuthHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${DIGISAKA_API_TOKEN}`,
  };
}

/** Laravel outbound routes (explorer-fields, mobile alerts, etc.). */
export const DIGISAKA_OUTBOUND_API_ORIGIN: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: { VITE_DIGISAKA_OUTBOUND_API_ORIGIN?: string } }).env
    ?.VITE_DIGISAKA_OUTBOUND_API_ORIGIN) || 'https://digisaka.app';

export type ExplorerFarmerOption = {
  farmer_id: number;
  name: string;
  label: string;
  mobile?: string | null;
  email?: string | null;
  farms_count?: number;
  member_id?: string;
  user_id?: number;
  /** Present on LGU monitoring farmers-by-city|province rows */
  province_id?: string | null;
  city_id?: string | null;
  province_name?: string | null;
  city_name?: string | null;
};

/** Parse a numeric Digisaka farmer_id from list rows or geometry properties. */
export function parseExplorerFarmerId(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}

/**
 * GET explorer-fields/{id} returns a FeatureCollection whose properties include farmer_id.
 * Field list rows often omit farmer_id, so geometry is the source of truth for create/split-save.
 */
export function farmerIdFromExplorerGeometryPayload(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null;
  const root = payload as Record<string, any>;
  const data = root.data && typeof root.data === 'object' && !Array.isArray(root.data) ? root.data : root;
  const feature =
    data?.type === 'FeatureCollection' && Array.isArray(data.features) ? data.features[0] : data;
  const props = feature?.properties && typeof feature.properties === 'object' ? feature.properties : {};
  return parseExplorerFarmerId(
    props.farmer_id ?? props.farmerId ?? data?.farmer_id ?? root.farmer_id
  );
}

/** True when a contact field has a real value (not empty / literal ``NULL``). */
export function isRecordedContactValue(value: string | null | undefined): boolean {
  if (value == null) return false;
  const trimmed = String(value).trim();
  if (!trimmed) return false;
  const token = trimmed.toUpperCase();
  return token !== 'NULL' && token !== 'N/A' && token !== 'NONE' && token !== '-';
}

/** True when a farmer row has a real mobile number (not empty / literal ``NULL``). */
export function isRecordedFarmerMobile(mobile: string | null | undefined): boolean {
  return isRecordedContactValue(mobile);
}

/** Farmer-card mobile line — never show the raw ``NULL`` string. */
export function formatFarmerMobileLabel(mobile: string | null | undefined): string {
  return isRecordedFarmerMobile(mobile) ? String(mobile).trim() : 'No Mobile Number';
}

/** Farmer-card field count — ``0`` / missing reads as no saved farms. */
export function formatFarmerFieldsLabel(farmsCount: number | null | undefined): string {
  const count = typeof farmsCount === 'number' && Number.isFinite(farmsCount) ? farmsCount : 0;
  if (count <= 0) return 'No saved fields.';
  return `${count} field${count === 1 ? '' : 's'}`;
}

/** Prefer ``name``; if empty, strip "(Farmer #id)" from label. */
export function farmerPersonName(farmer: ExplorerFarmerOption): string {
  const fromName = (farmer.name || '').replace(/\s+/g, ' ').trim();
  if (fromName) return fromName;
  return (farmer.label || '')
    .replace(/\s*\(Farmer\s*#?\d+\)\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Build farm name like "H.Pandey Farm 1" (first initial + last name, no space after the dot).
 */
export function buildFarmerFarmName(farmer: ExplorerFarmerOption): string {
  const raw = farmerPersonName(farmer);
  const parts = raw.split(' ').filter(Boolean);
  let shortName = `Farmer${farmer.farmer_id}`;
  if (parts.length >= 2) {
    const first = parts[0].replace(/\./g, '');
    const lastName = parts[parts.length - 1].replace(/\./g, '');
    shortName = `${first.charAt(0).toUpperCase()}.${lastName}`;
  } else if (parts.length === 1) {
    const word = parts[0].replace(/\./g, '');
    shortName = `${word.charAt(0).toUpperCase()}.${word}`;
  }

  const nextIndex = Math.max(1, (farmer.farms_count ?? 0) + 1);
  return `${shortName} Farm ${nextIndex}`;
}

export type FarmsByCityRow = {
  farm_id: number;
  farm_name: string;
  farmer_id?: number | null;
  farmer_full_name?: string | null;
  farm_coordinates?: unknown;
  /** Normalized for sidebar list compatibility */
  farmer_name?: string | null;
  province_id?: string;
  city_id?: string;
};

/**
 * Farms for one municipality/city.
 * GET /api/outbound/farms-by-city?city_id={code}&province={optional}
 */
export async function fetchFarmsByCity(
  cityId: string,
  provinceId?: string | null
): Promise<FarmsByCityRow[]> {
  const city = String(cityId || '').trim();
  if (!city) return [];

  const url = new URL(`${DIGISAKA_OUTBOUND_API_ORIGIN}/api/outbound/farms-by-city`);
  url.searchParams.set('city_id', city);
  const province = String(provinceId || '').trim();
  if (province && province !== 'all') {
    url.searchParams.set('province', province);
  }

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      ...getDigisakaAuthHeaders(),
    },
  });
  if (!response.ok) {
    throw new Error(`Farms by city failed (${response.status})`);
  }
  const body = (await response.json()) as { success?: boolean; data?: FarmsByCityRow[] };
  if (!Array.isArray(body.data)) return [];

  return body.data.map((row) => ({
    ...row,
    farmer_name: row.farmer_full_name ?? row.farmer_name ?? null,
    province_id: province && province !== 'all' ? province : row.province_id,
    city_id: city,
  }));
}

/**
 * Search Digisaka farmers for admin field assignment.
 * GET /api/webgis/explorer-farmers?search={name}&limit={optional}
 * (admin-only; proxies digisaka.app /api/outbound/explorer-farmers)
 */
export async function searchExplorerFarmers(
  search: string,
  limit = 20
): Promise<ExplorerFarmerOption[]> {
  const q = search.trim();
  if (!q) return [];

  const { default: apiClient } = await import('../services/api');
  const { data } = await apiClient.get<{ success?: boolean; data?: ExplorerFarmerOption[] }>(
    '/webgis/explorer-farmers',
    { params: { search: q, limit } }
  );
  if (!Array.isArray(data?.data)) return [];
  return data.data.filter(
    (f) => f && typeof f.farmer_id === 'number' && Number.isFinite(f.farmer_id)
  );
}

export type FieldCropFarmingPractice = {
  farming_practice_id?: number;
  crop_type_id?: number | null;
  crop_type?: string | null;
  sowing_date?: string | null;
  planting_date?: string | null;
  expected_harvest_date?: string | null;
  planting_method?: string | null;
  cropping_season?: string | null;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type FieldCropInformation = {
  farm_id?: number | string;
  farm_unique_id?: string | null;
  name?: string | null;
  farmer_id?: number | null;
  user_id?: number | null;
  total_land_area?: number | null;
  crop_type_id?: number | null;
  crop_type?: string | null;
  sowing_date?: string | null;
  farming_practices?: FieldCropFarmingPractice[];
};

export type CropCalendarFormValues = {
  crop_type: string;
  planting_method: string;
  sowing_date: string;
  planting_date: string;
  expected_harvest_date: string;
  cropping_season: string;
};

export type CropCalendarPrefill = CropCalendarFormValues & {
  crop_establishment_date: string;
  crop_variety: string;
};

export const EMPTY_CROP_CALENDAR_FORM: CropCalendarFormValues = {
  crop_type: '',
  planting_method: '',
  sowing_date: '',
  planting_date: '',
  expected_harvest_date: '',
  cropping_season: '',
};

export const CROP_CALENDAR_PLANTING_METHODS = [
  { value: 'Direct seeded', label: 'Direct seeded' },
  { value: 'Transplanted', label: 'Transplanted' },
] as const;

export const CROP_CALENDAR_SEASONS = [
  { value: 'First', label: 'First' },
  { value: 'Second', label: 'Second' },
  { value: 'Third', label: 'Third' },
] as const;

export function normalizePlantingMethod(value: string | null | undefined): string {
  const n = String(value || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .trim();
  if (n.includes('transplant')) return 'Transplanted';
  if (n.includes('direct') || n.includes('seed')) return 'Direct seeded';
  return String(value || '').trim();
}

export function formValuesFromFieldCrop(
  info: FieldCropInformation | null | undefined
): CropCalendarFormValues {
  const practice = pickPrimaryFarmingPractice(info);
  return {
    crop_type: String(practice?.crop_type || info?.crop_type || '').trim(),
    planting_method: normalizePlantingMethod(practice?.planting_method),
    sowing_date:
      toIsoDateInputValue(practice?.sowing_date) || toIsoDateInputValue(info?.sowing_date),
    planting_date: toIsoDateInputValue(practice?.planting_date),
    expected_harvest_date: toIsoDateInputValue(practice?.expected_harvest_date),
    cropping_season: String(practice?.cropping_season || '').trim(),
  };
}

export function establishmentDateFromForm(form: CropCalendarFormValues): string {
  const method = normalizePlantingMethod(form.planting_method);
  if (method === 'Transplanted' && form.planting_date.trim()) {
    return form.planting_date.trim();
  }
  return form.sowing_date.trim() || form.planting_date.trim();
}

export function isCropCalendarFormReady(form: CropCalendarFormValues): boolean {
  if (!form.crop_type.trim() || !form.planting_method.trim() || !form.sowing_date.trim()) {
    return false;
  }
  if (normalizePlantingMethod(form.planting_method) === 'Transplanted' && !form.planting_date.trim()) {
    return false;
  }
  return Boolean(establishmentDateFromForm(form));
}

export function toCropCalendarPrefill(form: CropCalendarFormValues): CropCalendarPrefill | null {
  if (!isCropCalendarFormReady(form)) return null;
  const establishment = establishmentDateFromForm(form);
  return {
    ...form,
    crop_establishment_date: establishment,
    crop_variety: form.crop_type.trim(),
  };
}

/** Map outbound field-crop records onto crop-calendar farmer inputs. */
export function calendarInputFromFieldCrop(
  info: FieldCropInformation | null | undefined
): CropCalendarPrefill | null {
  return toCropCalendarPrefill(formValuesFromFieldCrop(info));
}

/** Convert API dates (`MM/DD/YYYY` or ISO) to `YYYY-MM-DD` for `<input type="date">`. */
export function toIsoDateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  const s = String(value).trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) {
    return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatFieldCropDateLabel(value: string | null | undefined): string {
  const iso = toIsoDateInputValue(value);
  if (!iso) return '—';
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return '—';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function pickPrimaryFarmingPractice(
  info: FieldCropInformation | null | undefined
): FieldCropFarmingPractice | null {
  const practices = info?.farming_practices;
  if (!Array.isArray(practices) || practices.length === 0) return null;
  const ongoing = practices.find((p) => String(p?.status || '').toLowerCase() === 'ongoing');
  return ongoing ?? practices[0] ?? null;
}

export function fieldCropHasDisplayableInfo(info: FieldCropInformation | null | undefined): boolean {
  if (!info) return false;
  const practice = pickPrimaryFarmingPractice(info);
  return Boolean(
    info.crop_type ||
      info.sowing_date ||
      practice?.crop_type ||
      practice?.sowing_date ||
      practice?.planting_date ||
      practice?.planting_method
  );
}

/**
 * GET /api/outbound/field-crop-information?farm_id={farm_id}
 * Returns null when the farm has no crop record (or is not found).
 */
export async function fetchFieldCropInformation(
  farmId: string | number,
  signal?: AbortSignal
): Promise<FieldCropInformation | null> {
  const id = String(farmId ?? '').trim();
  if (!id || id === 'undefined' || id === 'null') return null;

  const url = new URL(`${DIGISAKA_OUTBOUND_API_ORIGIN}/api/outbound/field-crop-information`);
  url.searchParams.set('farm_id', id);

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
      ...getDigisakaAuthHeaders(),
    },
    signal,
  });

  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Field crop information failed (${response.status})`);
  }

  const body = (await response.json()) as {
    success?: boolean;
    data?: FieldCropInformation | null;
  };
  if (!body?.success || !body.data || typeof body.data !== 'object') return null;
  return body.data;
}

/** @deprecated Prefer always calling Digisaka ``/api/webgis/farms`` (server routes admin vs customer). */
export function usesWebgisFarmsList(
  user:
    | {
        is_superuser?: boolean;
        is_system_guest?: boolean;
        external_user_id?: string | null;
        role?: string;
      }
    | null
    | undefined
): boolean {
  if (!user) return false;
  if (user.is_superuser) return false;
  if (user.is_system_guest) return false;
  if (String(user.role || '').toLowerCase() === 'admin') return false;
  return true;
}

export type ExplorerFieldListRow = {
  farm_id: string | number;
  /** Public farm code from digisaka.app (e.g. FARM000015) — preferred for UI display. */
  farm_unique_id?: string | null;
  farm_name?: string;
  farmer_name?: string | null;
  farmer_id?: number | string | null;
  province_id?: string | null;
  city_id?: string | null;
  barangay_id?: string | number | null;
  created_at?: string | null;
  updated_at?: string | null;
  /** digisaka.app webgis farms — multi-block rings ``[[lng,lat],…]`` */
  polygons?: number[][][] | null;
  plot_count?: number | null;
  geometry?: { type?: string; coordinates?: unknown } | null;
  [key: string]: unknown;
};

/** Compact numeric farm code for UI (``FARM003134`` → ``3134``). */
export function compactFarmUniqueId(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const prefixed = /^FARM0*(\d+)$/i.exec(s);
  if (prefixed) return prefixed[1];
  const digits = s.replace(/\D/g, '');
  if (digits) return String(parseInt(digits, 10));
  return s;
}

/** digisaka.app internal farm row id (``id`` / ``farm_id``), not ``farm_unique_id``. */
export function farmInternalId(field: Record<string, unknown> | null | undefined): string {
  if (!field) return '';
  const id = field.farm_id ?? field.id;
  if (id != null && String(id).trim()) return String(id).trim();
  return '';
}

/** User-facing farm ID in sidebar / panels — always the internal ``id`` / ``farm_id``. */
export function farmPublicId(field: Record<string, unknown> | null | undefined): string {
  return farmInternalId(field);
}

/**
 * Label for sidebar / map.
 * Uses digisaka ``name`` when set; otherwise ``Farm {id}`` (internal id, not farm_unique_id).
 */
export function displayFarmName(field: Record<string, unknown> | null | undefined): string {
  if (!field) return 'Farm';
  const fid = farmInternalId(field);

  const rawName = String(field.name ?? '').trim();
  if (rawName) return rawName;

  const farmName = String(field.farm_name ?? '').trim();
  if (farmName) {
    const autoOnly = /^Farm (\d+)$/i.exec(farmName);
    // Ignore stale FastAPI fallbacks derived from farm_unique_id (e.g. id 29 → "Farm 15").
    if (!autoOnly || !fid || autoOnly[1] === fid) {
      return farmName;
    }
  }

  if (fid) return `Farm ${fid}`;
  return farmName || 'Farm';
}

/** Short block label for nested sidebar rows (parent field name is shown above). */
export function displayBlockLabel(blockNumber: number): string {
  return `Block ${blockNumber}`;
}

/** Full block label when farm + block context is needed (map, analysis, split). */
export function displayBlockContextLabel(
  field: Record<string, unknown> | null | undefined,
  blockNumber: number
): string {
  return `${displayFarmName(field)} · ${displayBlockLabel(blockNumber)}`;
}

/**
 * Convert digisaka.app farm ``polygons`` rings → GeoJSON Feature (Polygon or MultiPolygon).
 */
export function farmPolygonsToGeoJsonFeature(
  polygons: unknown,
  properties: Record<string, unknown> = {}
): { type: 'Feature'; properties: Record<string, unknown>; geometry: { type: string; coordinates: unknown } } | null {
  if (!Array.isArray(polygons) || polygons.length === 0) return null;
  const rings = polygons.filter(
    (ring): ring is number[][] =>
      Array.isArray(ring) &&
      ring.length >= 3 &&
      Array.isArray(ring[0]) &&
      typeof ring[0][0] === 'number'
  );
  if (!rings.length) return null;

  const geometry =
    rings.length === 1
      ? { type: 'Polygon', coordinates: [rings[0]] }
      : { type: 'MultiPolygon', coordinates: rings.map((ring) => [ring]) };

  return {
    type: 'Feature',
    properties: {
      ...properties,
      plot_count: properties.plot_count ?? rings.length,
    },
    geometry,
  };
}

/**
 * Prefer geometry already on the farms list row (polygons / geometry).
 * Avoids per-farm outbound calls when polygons are already on the list row.
 */
export function resolveFarmGeoJsonFromRow(
  field: ExplorerFieldListRow | Record<string, unknown> | null | undefined
): { type: 'Feature'; properties: Record<string, unknown>; geometry: { type: string; coordinates: unknown } } | null {
  if (!field || typeof field !== 'object') return null;
  const row = field as Record<string, unknown>;

  if (row.type === 'Feature' && row.geometry) {
    return row as {
      type: 'Feature';
      properties: Record<string, unknown>;
      geometry: { type: string; coordinates: unknown };
    };
  }

  const props: Record<string, unknown> = {
    farm_id: row.farm_id ?? row.id,
    farmer_id: row.farmer_id,
    name: displayFarmName(row),
    farm_name: displayFarmName(row),
    farm_unique_id: row.farm_unique_id,
    plot_count: row.plot_count,
  };

  const fromPolygons = farmPolygonsToGeoJsonFeature(row.polygons, props);
  if (fromPolygons) return fromPolygons;

  const geom = row.geometry;
  if (geom && typeof geom === 'object' && !Array.isArray(geom) && (geom as { type?: string }).type) {
    return {
      type: 'Feature',
      properties: props,
      geometry: geom as { type: string; coordinates: unknown },
    };
  }

  return null;
}

/**
 * Explode farm list rows into one GeoJSON Feature per polygon block for map overview.
 * Labels: ``{farm_name} · block1``, ``block2``, …
 */
export function farmRowsToBlockFeatureCollection(
  rows: Array<ExplorerFieldListRow | Record<string, unknown>> | null | undefined
): {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: Record<string, unknown>;
    geometry: { type: string; coordinates: unknown };
  }>;
  /** 1-based map marker numbers keyed by farm_id (farms with drawable geometry only). */
  markerByFarmId: Record<string, number>;
} {
  const features: Array<{
    type: 'Feature';
    properties: Record<string, unknown>;
    geometry: { type: string; coordinates: unknown };
  }> = [];
  const markerByFarmId: Record<string, number> = {};
  let nextMarker = 0;

  for (const row of rows || []) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    if (r.isDemo) continue;

    const farmId = r.farm_id ?? r.id;
    if (farmId == null || farmId === '') continue;
    const farmName = displayFarmName(r);

    let rings: number[][][] = [];
    if (Array.isArray(r.polygons) && r.polygons.length > 0) {
      rings = (r.polygons as unknown[]).filter(
        (ring): ring is number[][] =>
          Array.isArray(ring) &&
          ring.length >= 3 &&
          Array.isArray(ring[0]) &&
          typeof ring[0][0] === 'number'
      );
    } else {
      const resolved = resolveFarmGeoJsonFromRow(r);
      const g = resolved?.geometry;
      if (g?.type === 'Polygon' && Array.isArray(g.coordinates)) {
        const exterior = (g.coordinates as unknown[])[0];
        if (Array.isArray(exterior)) rings = [exterior as number[][]];
      } else if (g?.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
        for (const poly of g.coordinates as unknown[]) {
          if (!Array.isArray(poly) || !poly[0]) continue;
          rings.push(poly[0] as number[][]);
        }
      }
    }

    if (!rings.length) continue;

    const farmKey = String(farmId);
    if (markerByFarmId[farmKey] == null) {
      nextMarker += 1;
      markerByFarmId[farmKey] = nextMarker;
    }
    const markerNumber = markerByFarmId[farmKey];

    rings.forEach((ring, index) => {
      const blockNum = index + 1;
      // Short block-only label for close zoom (avoids farm-name overlap clutter).
      const blockLabel = rings.length === 1 ? farmName : `Block ${blockNum}`;
      features.push({
        type: 'Feature',
        properties: {
          farm_id: farmId,
          farm_unique_id: r.farm_unique_id,
          farmer_id: r.farmer_id,
          farm_name: farmName,
          name: farmName,
          block_index: index,
          block_number: blockNum,
          block_label: blockLabel,
          plot_count: rings.length,
          marker_number: markerNumber,
          is_marker_primary: index === 0,
          layer_role: 'farm_overview',
        },
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
    });
  }

  return { type: 'FeatureCollection', features, markerByFarmId };
}

export type FarmListPagination = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

export type FetchSavedFieldsOptions = {
  province_id?: string | null;
  city_id?: string | null;
  startdate?: string | null;
  enddate?: string | null;
  /** Admin/guest only — digisaka webgis-farms page (default 1). */
  page?: number;
  per_page?: number;
  /**
   * When true (default), ask FastAPI for every farm in one response
   * (`fetch_all=true` — server aggregates digisaka pages; no client pagination loop).
   */
  fetch_all_pages?: boolean;
  /** Admin/guest outbound list: ``summary`` (default) or ``full`` (all farm boundaries on map). */
  fields?: 'summary' | 'full';
  /** Admin/guest: server-side name / id / farmer search across the full catalog. */
  search?: string | null;
  /** Admin/guest: server-side alert risk filter — high, medium, or low. */
  risk_level?: 'high' | 'medium' | 'low' | null;
};

export type FetchSavedFieldsPageResult = {
  rows: ExplorerFieldListRow[];
  pagination: FarmListPagination | null;
  source?: string;
};

export type ExplorerAvailProvince = {
  province_id: string;
  province_name: string;
};

export type PhilippinesCityRow = {
  id?: number;
  city_code: string;
  city_name: string;
  province_code?: string;
};

/** Provinces that have farms — via FastAPI outbound proxy. */
export async function fetchExplorerAvailProvincesViaApi(): Promise<ExplorerAvailProvince[]> {
  const { default: apiClient } = await import('../services/api');
  const { data } = await apiClient.get<{ success?: boolean; data?: ExplorerAvailProvince[] }>(
    '/outbound/explorer-avail-provinces'
  );
  return Array.isArray(data?.data) ? data.data : [];
}

/** Cities in a province — via FastAPI outbound proxy. */
export async function fetchPhilippinesCitiesViaApi(
  provinceId: string
): Promise<PhilippinesCityRow[]> {
  const province = String(provinceId || '').trim();
  if (!province || province === 'all') return [];

  const { default: apiClient } = await import('../services/api');
  const { data } = await apiClient.get<{ success?: boolean; data?: PhilippinesCityRow[] }>(
    '/outbound/philippines-cities',
    { params: { province_id: province } }
  );
  return Array.isArray(data?.data) ? data.data : [];
}

function buildSavedFieldsListParams(options?: FetchSavedFieldsOptions): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  const province = String(options?.province_id || '').trim();
  const city = String(options?.city_id || '').trim();
  const startdate = String(options?.startdate || '').trim();
  const enddate = String(options?.enddate || '').trim();
  if (province && province !== 'all') params.province_id = province;
  if (city && city !== 'all') params.city_id = city;
  if (startdate) params.startdate = startdate;
  if (enddate) params.enddate = enddate;
  if (options?.page != null && Number.isFinite(options.page)) {
    params.page = Math.max(1, Math.floor(options.page));
  }
  if (options?.per_page != null && Number.isFinite(options.per_page)) {
    params.per_page = Math.max(1, Math.min(100, Math.floor(options.per_page)));
  }
  const fields = String(options?.fields || '').trim().toLowerCase();
  if (fields === 'full' || fields === 'summary') {
    params.fields = fields;
  }
  const search = String(options?.search || '').trim();
  if (search) params.search = search;
  const risk = String(options?.risk_level || '').trim().toLowerCase();
  if (risk === 'high' || risk === 'medium' || risk === 'low') {
    params.risk_level = risk;
  }
  return params;
}

type SavedFieldsApiResponse = {
  success?: boolean;
  data?: ExplorerFieldListRow[];
  pagination?: FarmListPagination | null;
  message?: string;
  source?: string;
};

/**
 * One page of saved fields. Admin/guest responses include ``pagination`` meta.
 */
export async function fetchSavedFieldsPageForUser(
  _user?: unknown,
  options?: FetchSavedFieldsOptions
): Promise<FetchSavedFieldsPageResult> {
  const { default: apiClient } = await import('../services/api');
  const params = buildSavedFieldsListParams(options);
  if (params.page == null) params.page = 1;
  if (params.per_page == null) params.per_page = 100;

  const { data } = await apiClient.get<SavedFieldsApiResponse>('/webgis/farms', { params });
  return {
    rows: Array.isArray(data?.data) ? data.data : [],
    pagination: data?.pagination ?? null,
    source: data?.source,
  };
}

/**
 * Unified saved-fields list via Digisaka FastAPI ``GET /api/webgis/farms``.
 * Admin/guest: one request with ``fetch_all=true`` (FastAPI aggregates upstream pages).
 * Customers/LGU: single upstream response (unchanged).
 */
export async function fetchSavedFieldsListForUser(
  _user?: unknown,
  options?: FetchSavedFieldsOptions
): Promise<ExplorerFieldListRow[]> {
  const { default: apiClient } = await import('../services/api');
  const params = buildSavedFieldsListParams(options);

  if (options?.fetch_all_pages !== false) {
    params.fetch_all = 'true';
  } else {
    if (params.page == null) params.page = options?.page ?? 1;
    if (params.per_page == null) params.per_page = options?.per_page ?? 100;
  }

  const { data } = await apiClient.get<SavedFieldsApiResponse>('/webgis/farms', { params });
  return Array.isArray(data?.data) ? data.data : [];
}

/**
 * Single farm detail via Digisaka FastAPI ``GET /api/webgis/farms/{id}``.
 * Admin/guest → outbound webgis-farms/{id}; customers → scoped list row.
 */
export async function fetchFarmDetailViaDigisakaApi(
  farmId: string | number
): Promise<ExplorerFieldListRow | null> {
  const { default: apiClient } = await import('../services/api');
  const { data } = await apiClient.get<{
    success?: boolean;
    data?: ExplorerFieldListRow;
    message?: string;
    source?: string;
  }>(`/webgis/farms/${encodeURIComponent(String(farmId))}`);
  return data?.data ?? null;
}

/**
 * Resolve GeoJSON from a list row, fetching full farm detail when polygons are omitted.
 */
export async function resolveFarmGeoJsonWithDetail(
  field: ExplorerFieldListRow | Record<string, unknown> | null | undefined
): Promise<ReturnType<typeof resolveFarmGeoJsonFromRow>> {
  const fromRow = resolveFarmGeoJsonFromRow(field);
  if (fromRow?.geometry) return fromRow;

  const farmId = (field as Record<string, unknown> | null | undefined)?.farm_id
    ?? (field as Record<string, unknown> | null | undefined)?.id;
  if (farmId == null || farmId === '') return null;

  try {
    const detail = await fetchFarmDetailViaDigisakaApi(farmId);
    return resolveFarmGeoJsonFromRow(detail ?? field);
  } catch {
    return null;
  }
}

/**
 * Farmer list via Digisaka FastAPI ``GET /api/webgis/farmers``
 * (proxies digisaka.app /api/webgis/farmers for the linked account).
 */
export async function fetchMonitoringFarmersListForUser(): Promise<ExplorerFarmerOption[]> {
  const { default: apiClient } = await import('../services/api');
  const { data } = await apiClient.get<{
    success?: boolean;
    data?: ExplorerFarmerOption[];
    message?: string;
    source?: string;
  }>('/webgis/farmers');
  return Array.isArray(data?.data) ? data.data : [];
}

/**
 * Create farm(s) via Digisaka ``POST /api/webgis/farms``.
 * Accepts a single outbound-style payload or ``{ farms: [...] }``.
 * Admin → outbound webgis-farms create; customers → digisaka.app webgis batch create.
 */
export async function createFarmsViaDigisakaApi(
  payload: Record<string, unknown> | { farms: Record<string, unknown>[] }
): Promise<unknown> {
  const { default: apiClient } = await import('../services/api');
  try {
    const { data } = await apiClient.post('/webgis/farms', payload);
    return data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { detail?: unknown }; status?: number } };
    const detail = ax.response?.data?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d?.msg || String(d)).join('; ')
          : 'Failed to save field';
    throw new Error(msg);
  }
}

/**
 * Update farm via Digisaka ``PUT /api/webgis/farms/{id}``.
 * Proxies to digisaka.app webgis PUT (customers).
 */
export async function updateFarmViaDigisakaApi(
  farmId: string | number,
  payload: Record<string, unknown>
): Promise<unknown> {
  const { default: apiClient } = await import('../services/api');
  try {
    const { data } = await apiClient.put(`/webgis/farms/${encodeURIComponent(String(farmId))}`, payload);
    return data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { detail?: unknown } } };
    const detail = ax.response?.data?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d?.msg || String(d)).join('; ')
          : 'Failed to update field';
    throw new Error(msg);
  }
}

/**
 * Delete farm via Digisaka ``DELETE /api/webgis/farms/{id}``.
 * Customers may delete only their own farms; admin may delete any; guests forbidden.
 */
export async function deleteFarmViaDigisakaApi(farmId: string | number): Promise<unknown> {
  const { default: apiClient } = await import('../services/api');
  try {
    const { data } = await apiClient.delete(`/webgis/farms/${encodeURIComponent(String(farmId))}`);
    return data;
  } catch (err: unknown) {
    const ax = err as { response?: { data?: { detail?: unknown } } };
    const detail = ax.response?.data?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d?.msg || String(d)).join('; ')
          : 'Failed to delete field';
    throw new Error(msg);
  }
}

/** @deprecated Use fetchSavedFieldsListForUser */
export async function fetchWebgisFarmsForCurrentUser(): Promise<ExplorerFieldListRow[]> {
  return fetchSavedFieldsListForUser();
}
